/** 管理者／工程師：監控＋績效（outcomes 鏡像、人工數、節點心跳）。 */
import { useEffect, useState } from 'react';
import { getMonitor, listFleetNodes, type FleetNode } from '../../lib/fleetApi';
import { fillFunnel } from '../../lib/funnel';
import { fleetNodeServiceKeys, summarizeFleetLive } from '../../lib/fleetNodes';

const REFRESH_MS = 15_000;

function cell(value: string): string {
  return value === '' ? '—' : value;
}

function NodesTable({ nodes }: { nodes: FleetNode[] }) {
  if (nodes.length === 0) {
    return (
      <p className="mt-2 text-sm text-text-tertiary" data-testid="monitor-nodes-empty">
        目前沒有回報的節點。
      </p>
    );
  }

  return (
    <div className="mt-2 overflow-x-auto" data-testid="monitor-nodes">
      <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-text-tertiary">
            <th className="px-2 py-1 font-medium">nodeId</th>
            <th className="px-2 py-1 font-medium">host</th>
            <th className="px-2 py-1 font-medium">lastSeenAt</th>
            <th className="px-2 py-1 font-medium">deploySha</th>
            <th className="px-2 py-1 font-medium">services</th>
            <th className="px-2 py-1 font-medium">live</th>
            <th className="px-2 py-1 font-medium">狀態</th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => {
            const services = fleetNodeServiceKeys(node.services).join(', ');
            return (
              <tr
                key={node.nodeId}
                className="border-t border-border font-mono text-xs"
                data-testid="monitor-node-row"
                data-node-id={node.nodeId}
                data-stale={node.stale ? 'true' : 'false'}
              >
                <td className="px-2 py-2">{node.nodeId}</td>
                <td className="px-2 py-2">{cell(node.host)}</td>
                <td className="px-2 py-2">
                  {node.lastSeenAt === '' ? '—' : <time dateTime={node.lastSeenAt}>{node.lastSeenAt}</time>}
                </td>
                <td className="px-2 py-2">
                  <div data-testid="monitor-node-deploy-sha">{cell(node.deploySha ?? '')}</div>
                  {node.deployLabel ? <div className="text-text-tertiary">{node.deployLabel}</div> : null}
                </td>
                <td className="px-2 py-2">{cell(services)}</td>
                <td className="px-2 py-2" data-testid="monitor-node-live">
                  {cell(summarizeFleetLive(node.live, node.liveStatus))}
                </td>
                <td className="px-2 py-2">
                  {node.stale ? (
                    <span className="inline-flex rounded-full bg-text-tertiary/15 px-2 py-0.5 text-[10px] text-danger" data-testid="monitor-node-stale">
                      過期
                    </span>
                  ) : (
                    <span className="text-text-tertiary">活著</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function MonitorPage() {
  const [data, setData] = useState<Awaited<ReturnType<typeof getMonitor>> | null>(null);
  const [nodes, setNodes] = useState<FleetNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nodesError, setNodesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [monitorResult, nodesResult] = await Promise.allSettled([getMonitor(24), listFleetNodes()]);
      if (cancelled) return;
      if (monitorResult.status === 'fulfilled') {
        setData(monitorResult.value);
        setError(null);
      } else {
        const reason: unknown = monitorResult.reason;
        setError(reason instanceof Error ? reason.message : String(reason));
      }
      if (nodesResult.status === 'fulfilled') {
        setNodes(nodesResult.value);
        setNodesError(null);
      } else {
        const reason: unknown = nodesResult.reason;
        setNodesError(reason instanceof Error ? reason.message : String(reason));
      }
    };
    void load();
    const id = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const outcomeMap = Object.fromEntries((data?.outcomes ?? []).map((row) => [row.event, row.n]));
  const ready = data !== null && nodes !== null;

  return (
    <div className="flex min-w-0 flex-col gap-4 p-3" data-testid="monitor-page" data-ready={ready ? 'true' : 'false'}>
      <header>
        <p className="text-[11px] uppercase tracking-widest text-text-tertiary">監控／績效</p>
        <h1 className="font-serif text-xl">控制面總覽（24h）</h1>
        <p className="mt-1 text-xs text-text-tertiary">
          資料來自 chat-rea-core；kg SSH 斷線仍可看。AI 成效詳情仍在「系統 › AI」。
        </p>
      </header>
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {data === null && error === null ? (
        <p className="text-sm text-text-tertiary" role="status">
          載入中…
        </p>
      ) : null}
      {data === null ? null : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="monitor-kpis">
            {[
              ['link_sent', outcomeMap.link_sent ?? 0],
              ['click', outcomeMap.click ?? 0],
              ['signup', outcomeMap.signup ?? 0],
              ['messages', data.messages],
              ['human_open', data.humanOpen],
              ['nodes_alive', data.nodesAlive],
              ['params_v', data.paramsVersion],
              ['order', outcomeMap.order ?? 0],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded border border-border bg-surface p-3">
                <div className="text-[10px] uppercase tracking-wider text-text-tertiary">{label}</div>
                <div className="mt-1 font-mono text-2xl">{value}</div>
              </div>
            ))}
          </div>
          <section aria-labelledby="monitor-funnel-heading" data-testid="monitor-funnel">
            <h2 id="monitor-funnel-heading" className="text-sm font-medium">
              導流漏斗（<span data-testid="monitor-funnel-window">{data.hours}h</span>）
            </h2>
            <p className="mt-1 text-xs text-text-tertiary" data-testid="monitor-funnel-mirror-note">
              控制面鏡像，不是 kg 帳本。
            </p>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[28rem] border-collapse text-left text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-tertiary">
                    <th className="px-2 py-1 font-medium">platform</th>
                    <th className="px-2 py-1 font-medium">link_sent</th>
                    <th className="px-2 py-1 font-medium">click</th>
                    <th className="px-2 py-1 font-medium">signup</th>
                  </tr>
                </thead>
                <tbody>
                  {fillFunnel(data.byPlatform).map((row) => (
                    <tr
                      key={row.platform}
                      className="border-t border-border font-mono text-xs"
                      data-testid="monitor-funnel-row"
                      data-platform={row.platform}
                    >
                      <td className="px-2 py-2">
                        <a
                          href={`#/waterfall?platform=${encodeURIComponent(row.platform)}`}
                          className="inline-flex rounded border border-border px-2 py-0.5 text-[10px] text-text"
                          data-testid={`monitor-funnel-platform-${row.platform}`}
                        >
                          {row.platform}
                        </a>
                      </td>
                      <td className="px-2 py-2" data-testid="monitor-funnel-link-sent">
                        {row.link_sent}
                      </td>
                      <td className="px-2 py-2" data-testid="monitor-funnel-click">
                        {row.click}
                      </td>
                      <td className="px-2 py-2" data-testid="monitor-funnel-signup">
                        {row.signup}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h2 className="text-sm font-medium">模式分布</h2>
            <ul className="mt-2 space-y-1 text-sm">
              {data.modes.map((mode) => (
                <li key={mode.mode} className="font-mono">
                  {mode.mode}: {mode.n}
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2 className="text-sm font-medium">目前參數摘要</h2>
            <pre className="mt-2 overflow-auto rounded border border-border p-2 font-mono text-[11px]">
              {JSON.stringify(data.params, null, 2)}
            </pre>
          </section>
        </>
      )}
      <section aria-labelledby="monitor-nodes-heading" data-testid="monitor-nodes-region" tabIndex={0}>
        <h2 id="monitor-nodes-heading" className="text-sm font-medium">
          機群節點
        </h2>
        {nodesError ? (
          <p className="mt-2 text-sm text-danger" role="alert">
            {nodesError}
          </p>
        ) : null}
        {nodes === null && nodesError === null ? (
          <p className="mt-2 text-sm text-text-tertiary" role="status">
            載入節點…
          </p>
        ) : null}
        {nodes === null ? null : <NodesTable nodes={nodes} />}
      </section>
    </div>
  );
}
